import { useEffect } from "react";

const FILTER_KEY="arcvault-metric-filter";
const text=(el:Element|null)=>(el?.textContent||"").replace(/\s+/g," ").trim();
const firstLabel=(card:Element)=>text(card.querySelector("span"))||text(card).split(/\d+/)[0].trim();
const normalized=(value:string)=>value.toUpperCase().replace(/\s+/g," ").trim();
const rowStatus=(row:HTMLElement)=>normalized(text(row.querySelector(".status")));

function rowsFor(content:Element){
 return [...content.querySelectorAll<HTMLElement>("tbody tr, .ncr-card, .project-card, .record-list article, .requirement-card, .qualification-cards article")];
}

function clear(content:Element){
 content.querySelectorAll(".metric-card-selected").forEach(x=>x.classList.remove("metric-card-selected"));
 rowsFor(content).forEach(x=>x.style.display="");
 content.querySelector<HTMLElement>("[data-metric-filter-empty]")?.remove();
}

function qualificationMatch(label:string,row:HTMLElement){
 const t=normalized(text(row));
 const s=rowStatus(row);
 if(label.includes("EXPIRING"))return s.includes("EXPIRING");
 if(label==="EXPIRED"||label.includes("EXPIRED QUALIFICATION"))return s.includes("EXPIRED")&&!s.includes("EXPIRING");
 if(label.includes("ACTIVE QUALIFICATION"))return s.includes("ACTIVE")&&!s.includes("EXPIRING")&&!s.includes("EXPIRED");
 if(label.includes("ACTIVE WELDER"))return !s.includes("EXPIRED")||s.includes("ACTIVE")||s.includes("EXPIRING");
 if(label.includes("ALUMINUM"))return t.includes("ALUMINUM")||t.includes("W47.2");
 if(label.includes("STEEL"))return t.includes("STEEL")||t.includes("W47.1");
 return null;
}

function explicitMatch(label:string,row:HTMLElement){
 const t=normalized(text(row));
 const s=rowStatus(row);

 // Cards whose meaning is "show everything".
 if(label.startsWith("TOTAL ")||label==="ALL"||label.startsWith("ALL "))return true;

 // WPS / WPDS library.
 if(label==="ACTIVE")return s.includes("ACTIVE")&&!s.includes("INACTIVE");
 if(label.includes("PENDING REVIEW"))return s.includes("PENDING")||t.includes("PENDING");
 if(label.includes("SUPERSEDED"))return s.includes("SUPERSEDED")||t.includes("SUPERSEDED");

 // Projects.
 if(label.includes("OPEN PROJECT"))return s.includes("ACTIVE")||s.includes("PLANNING");
 if(label.includes("IN PRODUCTION"))return s.includes("ACTIVE");
 if(label==="PLANNING"||label.includes("PLANNING PROJECT"))return s.includes("PLANNING");
 if(label.includes("COMPLETE"))return s.includes("COMPLETE");

 // MTRs and inspection-type records.
 if(label.includes("VERIFIED"))return s.includes("VERIFIED")||t.includes("VERIFIED");
 if(label.includes("REJECTED"))return s.includes("REJECTED")||t.includes("REJECTED");
 if(label.includes("ACCEPTED"))return s.includes("ACCEPTED")||t.includes("ACCEPTED");
 if(label.includes("OPEN HOLD"))return (t.includes("HOLD POINT")||t.includes("HOLD"))&&(s.includes("PENDING")||s.includes("OPEN")||t.includes("PENDING"));

 // NCRs.
 if(label.includes("OVERDUE"))return t.includes("OVERDUE")||t.includes("LATE");
 if(label.includes("OPEN NCR")||label==="OPEN")return !s.includes("CLOSED")&&(s.includes("OPEN")||s.includes("PENDING")||t.includes("OPEN"));
 if(label.includes("CLOSED"))return s.includes("CLOSED")||t.includes("CLOSED");

 // Equipment / calibration.
 if(label.includes("CALIBRATION")&&label.includes("DUE"))return t.includes("DUE")||t.includes("EXPIR");
 if(label.includes("OVERDUE CALIBRATION"))return t.includes("OVERDUE")||t.includes("EXPIRED");

 return null;
}

function fallbackMatch(label:string,row:HTMLElement){
 const t=normalized(text(row));
 const words=label.replace(/[≤≥0-9]/g," ").split(/\s+/).filter(w=>w.length>3&&!['ACTIVE','TOTAL','CURRENT','RECORDS','DOCUMENTS','PROJECTS'].includes(w));
 if(!words.length)return null;
 return words.some(w=>t.includes(w));
}

function matchCard(label:string,row:HTMLElement){
 const q=qualificationMatch(label,row);if(q!==null)return q;
 const explicit=explicitMatch(label,row);if(explicit!==null)return explicit;
 return fallbackMatch(label,row);
}

function apply(card:HTMLElement,labelRaw?:string){
 const content=card.closest(".content")||document.querySelector("main .content");if(!content)return;
 const label=normalized(labelRaw||firstLabel(card));
 const already=card.classList.contains("metric-card-selected");
 clear(content);
 if(already){sessionStorage.removeItem(FILTER_KEY);return}
 card.classList.add("metric-card-selected");

 // Audit summary cards represent evidence sections rather than one shared record list.
 if(card.closest(".audit-metrics")){
   const heading=label.includes("ENGINEER")?"Welding Engineer Visits":label.includes("SUBCONTRACT")?"Subcontracted Companies":label.includes("PAST AUDIT")?"Past CWB Audits":label.includes("WELDER COUNT")?"Monthly Welder Counts":"";
   if(heading){const h=[...content.querySelectorAll("h2")].find(x=>text(x)===heading);h?.closest(".audit-section")?.scrollIntoView({behavior:"smooth",block:"start"})}
   return;
 }

 const rows=rowsFor(content);if(!rows.length)return;
 let shown=0;
 rows.forEach(row=>{const match=matchCard(label,row);const show=match===null?true:match;row.style.display=show?"":"none";if(show)shown++});
 if(!shown){
   const holder=content.querySelector(".table-card,.ncr-list,.project-grid,.record-list,.qualification-cards")||content;
   const empty=document.createElement("div");empty.dataset.metricFilterEmpty="true";empty.className="metric-filter-empty";empty.textContent=`No records match ${firstLabel(card)}.`;holder.appendChild(empty);
 }
}

export default function MetricCardFilterManager(){
 useEffect(()=>{
  const markCards=()=>document.querySelectorAll<HTMLElement>(".metrics article,.audit-metrics article").forEach(card=>{card.setAttribute("role","button");card.tabIndex=0;card.setAttribute("aria-label",`Filter by ${firstLabel(card)}`)});
  const onClick=(event:Event)=>{
   const target=event.target as HTMLElement;
   const card=target.closest(".metrics article,.audit-metrics article") as HTMLElement|null;
   if(card){apply(card);return}
   const dashboard=target.closest(".dashboard-metrics button") as HTMLElement|null;
   if(dashboard){sessionStorage.setItem(FILTER_KEY,firstLabel(dashboard));return}
  };
  const onKey=(event:KeyboardEvent)=>{
   if(event.key!=="Enter"&&event.key!==" ")return;
   const card=(event.target as HTMLElement).closest(".metrics article,.audit-metrics article") as HTMLElement|null;
   if(card){event.preventDefault();apply(card)}
  };
  const applyPending=()=>{
   markCards();
   const pending=sessionStorage.getItem(FILTER_KEY);if(!pending)return;
   const cards=[...document.querySelectorAll<HTMLElement>(".metrics article,.audit-metrics article")];
   const p=normalized(pending);
   const candidate=cards.find(c=>{const label=normalized(firstLabel(c));return label===p||label.includes(p)||p.includes(label)});
   if(candidate){sessionStorage.removeItem(FILTER_KEY);apply(candidate,pending)}
  };
  document.addEventListener("click",onClick,true);document.addEventListener("keydown",onKey,true);
  markCards();const timer=window.setInterval(applyPending,250);
  return()=>{document.removeEventListener("click",onClick,true);document.removeEventListener("keydown",onKey,true);clearInterval(timer)};
 },[]);
 return null;
}
